import { Component, inject, Input, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { format } from 'date-fns';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import {
  ColDef,
  ColGroupDef,
  FilterModel,
  GridOptions,
  IServerSideGetRowsRequest,
} from 'ag-grid-community';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { DialogService } from '@sui/dialog';

import { ClientAdminApiService } from 'src/app/api/services/client-admin-api.service';
import { ViewId } from 'src/app/api/services/view-preset-api.service';
import { ObjectEditorService } from 'src/app/core/services/object-editor.service';
import { NomenclatureService } from 'src/app/core/services/nomenclature.service';

import {
  AgButtonCellRendererComponent,
  AgBadgeCellRendererComponent,
  AgDateRangeFilterComponent,
  gridColumnHeader,
  DataGridModule,
} from '@sensika/ag-grid-kit';
import { DataGridHostBase } from 'src/app/ag-grid/data-grid-host-base';
import { AgBooleanCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-boolean-cell-renderer/ag-boolean-cell-renderer.component';
import { AgDataObjectCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-data-object-cell-renderer/ag-data-object-cell-renderer.component';
import { buildNomenclatureColumnDef } from 'src/app/ag-grid/utils/nomenclature-column.utils';
import { DrillFilters } from 'src/app/ag-grid/utils/drill-filters.utils';
import { actionTagVariant } from 'src/app/shared/utils/badge-variants';
import { JsonViewerDialogComponent } from 'src/app/shared/components/json-viewer-dialog/json-viewer-dialog.component';

import { UtilsLogApiService } from 'src/app/api/services/utils-log-api.service';
import { userActionLogExtraMetadataQuerySource } from 'src/app/api/queries/utils-log.queries';
import { userActionLogExtraMetadataPinnedFields } from 'src/app/shared/utils/user-action-log-metadata.utils';

import { AppState } from 'src/app/core/state/app.state';
import { selectUserAccessLevelGlobalAdmin } from 'src/app/core/state';
import { OBJECT_SETTINGS_ACCESS_LEVEL } from 'src/app/core/object-settings-access-level.constants';
import { NomObjectClass } from 'src/app/core/constants';

/** The `AgDateRangeFilterComponent` wire model format (agDateColumnFilter inRange). */
const DATE_FILTER_FORMAT = 'yyyy-MM-dd HH:mm:ss';

/**
 * Dashboard drill-down (WAP-1763): `?start_time=&end_time=` (unix seconds) becomes the
 * Date/Time range filter, `?action_status=2,3` the action status set filter -
 * ordinary column filters over the opening preset, visible and clearable.
 */
function drillFiltersOf(params: ParamMap): FilterModel {
  const model: FilterModel = {};
  const from = Number(params.get('start_time'));
  const to = Number(params.get('end_time'));
  if (Number.isInteger(from) && Number.isInteger(to) && from > 0 && to > from) {
    model['time_created.absolute'] = {
      filterType: 'date',
      type: 'inRange',
      dateFrom: format(from * 1000, DATE_FILTER_FORMAT),
      dateTo: format(to * 1000, DATE_FILTER_FORMAT),
    };
  }
  const statuses = (params.get('action_status') ?? '')
    .split(',')
    .filter((id) => /^\d+$/.test(id));
  if (statuses.length) {
    model['action_status.id'] = { filterType: 'set', values: statuses };
  }
  return model;
}

@Component({
  selector: 'app-user-activity-logs',
  templateUrl: './user-activity-logs.component.html',
  styleUrls: ['./user-activity-logs.component.scss'],
  imports: [DataGridModule],
})
export class UserActivityLogsComponent
  extends DataGridHostBase
  implements OnInit
{
  /** i18n scanner K4: registers 'user-activity-logs.grid.column.*' as used. */
  protected readonly i18nViewName = 'user-activity-logs';

  @Input() userId: string;
  @Input() objectId: string;
  @Input() objectClassId: number;
  @Input() activeCompanyId: string;
  /** Audit-trail embedding: per-object trail with most columns hidden. */
  @Input() isAuditTrail: boolean = false;

  private readonly store = inject<Store<AppState>>(Store);
  private readonly clientAdminApi = inject(ClientAdminApiService);
  private readonly editorService = inject(ObjectEditorService);
  private readonly nomenclatureService = inject(NomenclatureService);
  private readonly dialogService = inject(DialogService);
  private readonly utilsLogApi = inject(UtilsLogApiService);
  private readonly route = inject(ActivatedRoute);

  /** Dashboard drill-down filters (`?from&to&action_status`), routed use only. */
  private drill = new DrillFilters();
  gridOptionsOverrides: Partial<GridOptions> = {};
  readonly restoreDrillFilters = (): void => this.drill.restore(this.grid);

  readonly viewId = ViewId.ActivityLog;
  readonly title = this.translateService.instant(
    'user-activity-logs.title.user-activity-logs',
  );
  readonly icon: IconProp = ['fas', 'clipboard-list'];

  private isGlobalAdmin: boolean = false;

  /** Built in ngOnInit: the defs depend on the isAuditTrail input. */
  columnDefs: Array<ColDef | ColGroupDef> = [];

  ngOnInit() {
    // Subscribe to global admin access level
    this.store
      .select(selectUserAccessLevelGlobalAdmin)
      .subscribe((isGlobalAdmin) => {
        this.isGlobalAdmin = isGlobalAdmin;
      });

    // The errors-per-hour widget of the dashboards links here with the clicked
    // bucket and the error statuses; embedded (audit trail) uses read no URL.
    if (!this.isAuditTrail) {
      this.drill = new DrillFilters(
        drillFiltersOf(this.route.snapshot.queryParamMap),
      );
      this.gridOptionsOverrides = this.drill.gridOptions();
    }
    this.columnDefs = this.buildColumnDefs();
  }

  readonly fetchRows = (request: IServerSideGetRowsRequest): Observable<any> =>
    this.clientAdminApi.getUserActivityLogsAgGridModel(
      request,
      this.userId,
      this.objectId,
      this.objectClassId,
      this.activeCompanyId,
    );

  private columnHeader(key: string): string {
    return gridColumnHeader(this.translateService, this.i18nViewName, key);
  }

  private buildColumnDefs(): Array<ColDef | ColGroupDef> {
    return [
      {
        headerName: this.columnHeader('id'),
        field: 'id',
        filter: 'agNumberColumnFilter',
        // Primary sort: unique, totally orders records within the same second.
        sort: 'desc',
        sortIndex: 0,
        minWidth: 100,
        maxWidth: this.isAuditTrail ? null : 110,
        hide: this.isAuditTrail,
      },
      {
        headerName: this.columnHeader('date-time'),
        field: 'time_created.absolute',
        filter: AgDateRangeFilterComponent,
        sort: 'desc',
        sortIndex: 1,
        maxWidth: 200,
      },
      {
        headerName: this.columnHeader('company-details'),
        marryChildren: true,
        children: [
          {
            headerName: this.columnHeader('user-company'),
            field: 'user.company.name',
            filter: 'agTextColumnFilter',
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              object: 'user.company',
              clickAccessLevel:
                OBJECT_SETTINGS_ACCESS_LEVEL[NomObjectClass.COMPANY],
              onClick: (company) => this.editorService.editCompany(company?.id),
            },
            cellStyle: { overflow: 'visible' },
            hide: this.isAuditTrail,
          },
          {
            headerName: this.columnHeader('is-active'),
            field: 'user.company_is_enabled',
            minWidth: 60,
            maxWidth: 110,
            filterParams: {
              values: ['true', 'false'],
            },
            cellRenderer: AgBooleanCellRendererComponent,
            cellRendererParams: {
              isActive: 'data.user.company_is_enabled',
            },
            hide: this.isAuditTrail,
          },
        ],
      },
      {
        headerName: this.columnHeader('user-details'),
        marryChildren: true,
        children: [
          {
            headerName: this.columnHeader('user-name'),
            field: 'user',
            filter: 'agTextColumnFilter',
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              object: 'user',
              clickAccessLevel:
                OBJECT_SETTINGS_ACCESS_LEVEL[NomObjectClass.USER],
              onClick: (user) => this.editorService.editUser(user.id),
            },
            cellStyle: { overflow: 'visible' },
            maxWidth: this.isAuditTrail ? null : 280,
          },
          {
            headerName: this.columnHeader('is-active'),
            field: 'user.is_enabled',
            minWidth: 60,
            maxWidth: 100,
            filterParams: {
              values: ['true', 'false'],
            },
            cellRenderer: AgBooleanCellRendererComponent,
            cellRendererParams: {
              isActive: 'data.user.is_enabled',
            },
            hide: this.isAuditTrail,
          },
          {
            headerName: this.columnHeader('email'),
            field: 'user.email',
            filter: 'agTextColumnFilter',
            maxWidth: 280,
            hide: this.isAuditTrail,
          },
          {
            headerName: this.columnHeader('ip-address'),
            field: 'user_ip_address',
            filter: 'agTextColumnFilter',
            maxWidth: 130,
            hide: this.isAuditTrail,
          },
        ],
      },
      {
        headerName: this.columnHeader('object-details'),
        marryChildren: true,
        children: [
          {
            ...buildNomenclatureColumnDef(
              this.translateService,
              this.nomenclatureService,
              {
                headerNameKey: 'grid.column.object-details.class',
                fieldKey: 'object_class_id',
                fieldValue: 'object_class_name',
                nomenclatureName: 'object_class',
                otherFilterParams: {
                  defaultToNothingSelected: true,
                },
              },
            ),
            minWidth: 110,
            maxWidth: 150,
            hide: this.isAuditTrail,
          },
          {
            headerName: this.columnHeader('type'),
            field: 'object_type_name',
            filter: 'agTextColumnFilter',
            maxWidth: 150,
            hide: this.isAuditTrail,
          },
          {
            headerName: this.columnHeader('name'),
            field: 'object_name',
            filter: 'agTextColumnFilter',
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              object: 'object',
              // The log lists every object class; only refs with a UI target
              // (URL or settings dialog) render as links.
              clickable: (ref) => this.editorService.canOpenDataObjectRef(ref),
              onClick: (ref) => this.editorService.openDataObjectRef(ref),
            },
            cellStyle: { overflow: 'visible' },
            hide: this.isAuditTrail,
          },
          {
            headerName: this.columnHeader('object-company'),
            field: 'object_company.name',
            filter: 'agTextColumnFilter',
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              object: 'object_company',
              clickAccessLevel:
                OBJECT_SETTINGS_ACCESS_LEVEL[NomObjectClass.COMPANY],
              onClick: (company) => this.editorService.editCompany(company?.id),
            },
            cellStyle: { overflow: 'visible' },
            hide: this.isAuditTrail,
          },
          {
            headerName: this.columnHeader('is-active'),
            field: 'object_company.is_enabled',
            minWidth: 60,
            maxWidth: 100,
            filterParams: {
              values: ['true', 'false'],
            },
            cellRenderer: AgBooleanCellRendererComponent,
            cellRendererParams: {
              isActive: 'data.object_company.is_enabled',
            },
            hide: this.isAuditTrail,
          },
        ],
      },
      {
        ...buildNomenclatureColumnDef(
          this.translateService,
          this.nomenclatureService,
          {
            headerNameKey: 'grid.column.action-name',
            fieldKey: 'action_type.id',
            fieldValue: 'action_type.name',
            nomenclatureName: 'action_type',
            otherFilterParams: {
              defaultToNothingSelected: true,
            },
          },
        ),
        cellRenderer: AgBadgeCellRendererComponent,
        cellRendererParams: {
          nomField: 'action_type',
          textKey: 'value',
          variantOf: (action) => actionTagVariant(action.style),
        },
        minWidth: 180,
        maxWidth: 200,
      },
      {
        headerName: this.columnHeader('algorithm'),
        field: 'algorithm_name',
        filter: 'agTextColumnFilter',
        hide: this.isAuditTrail,
      },
      {
        ...buildNomenclatureColumnDef(
          this.translateService,
          this.nomenclatureService,
          {
            headerNameKey: 'grid.column.action-status',
            fieldKey: 'action_status.id',
            fieldValue: 'action_status.name',
            nomenclatureName: 'action_status',
            otherFilterParams: {
              defaultToNothingSelected: true,
            },
          },
        ),
        cellRenderer: AgBadgeCellRendererComponent,
        cellRendererParams: {
          nomField: 'action_status',
          textKey: 'value',
          variantOf: (action) => actionTagVariant(action.style),
        },
        minWidth: 100,
        maxWidth: 120,
        hide: this.isAuditTrail,
      },
      {
        headerName: this.columnHeader('extra-metadata'),
        field: 'has_extra_metadata',
        minWidth: 60,
        maxWidth: 100,
        filterParams: {
          values: ['true', 'false'],
        },
        cellRendererSelector: (params) => {
          const hasMetadata = Number(params?.data?.has_extra_metadata) === 1;

          if (hasMetadata) {
            // * show button when metadata exists
            return {
              component: AgButtonCellRendererComponent,
              params: {
                variant: 'primary',
                leadingIcon: faCheck,
                disabled: !this.isGlobalAdmin,
                action: this.getUserActionLogExternalMetadata.bind(this),
              },
            };
          } else {
            // * show X icon when no metadata
            return {
              component: AgBooleanCellRendererComponent,
              params: {
                isActive: false,
              },
            };
          }
        },
        hide: this.isAuditTrail,
      },
      {
        headerName: this.columnHeader('status-note'),
        field: 'action_status_code_name',
        filter: 'agTextColumnFilter',
        minWidth: 60,
        maxWidth: 120,
        hide: this.isAuditTrail,
      },
      {
        headerName: this.translateService.instant('grid.column.is-scheduled'),
        field: 'is_scheduled',
        minWidth: 100,
        maxWidth: 130,
        filterParams: {
          values: ['true', 'false'],
        },
        cellRenderer: AgBooleanCellRendererComponent,
        cellRendererParams: {
          isActive: 'data.is_scheduled',
        },
        hide: this.isAuditTrail,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.schedule-run-id',
        ),
        field: 'schedule_run_id',
        filter: 'agNumberColumnFilter',
        minWidth: 120,
        maxWidth: 150,
        type: 'rightAligned',
        initialHide: true,
        hide: this.isAuditTrail,
      },
    ];
  }

  getUserActionLogExternalMetadata(rowData: {
    id: number | string;
    has_extra_metadata: number | boolean;
  }): void {
    const userActionLogId = rowData?.id;
    const hasExtraMetadata = rowData?.has_extra_metadata;

    // * only open dialog when has_extra_metadata === 1
    if (Number(hasExtraMetadata) !== 1) {
      return;
    }

    this.dialogService.openSimpleDialog(JsonViewerDialogComponent, {
      data: {
        title: this.translateService.instant('user-action-log-metadata.title'),
        source: userActionLogExtraMetadataQuerySource(
          this.utilsLogApi,
          userActionLogId,
        ),
        pinned: userActionLogExtraMetadataPinnedFields(this.translateService),
      },
      closeOnClick: false,
      closeOnEscape: true,
      height: '70%',
      minHeight: 800,
      maxHeight: 1220,
    });
  }
}
